/**
 * LeftPanel — Document list panel (left side of Home).
 *
 * Layout (top to bottom):
 *  1. Header — Avatar dropdown + BridgeHealthIndicator + Select/Cancel button
 *  2. Search — Input with SearchLg icon
 *  3. Filters row — Select-all checkbox (if selection mode) + filter dropdowns + sort dropdown
 *  4. Document List — Grouped by source file, scrollable
 *  5. Upload Button — Pinned at bottom
 */
import { ArrowLeft, ChevronDown, CursorClick01, LogOut01, Package, SearchLg, SwitchVertical01, Upload01, User01, XClose } from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";
import { BridgeHealthIndicator } from "@/components/application/bridge-health-indicator";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import type { BridgeStatus } from "@/hooks/use-bridge-health";
import type { HomeSourceFile } from "@/types/home";
import { cx } from "@/utils/cx";
import { STATUS_OPTIONS } from "./constants";
import { DocumentListItem } from "./document-list-item";
import { FileGroupHeader } from "./file-group-header";
import { FilterDropdown, type FilterOption } from "./filter-dropdown";
import { isDocumentSelectable } from "./helpers";

/** Status filter options for the dropdown (excludes the 'all' entry). */
const STATUS_FILTER_OPTIONS: FilterOption[] = STATUS_OPTIONS.filter((o) => o.id !== "all").map((o) => ({ id: String(o.id), label: o.label }));

interface LeftPanelProps {
    /** Display name of the logged-in user */
    userName?: string;
    /** Initials for the avatar */
    userInitials?: string;
    /** Filtered & sorted source files with their documents */
    sourceFiles: HomeSourceFile[];
    /** Currently viewed document ID */
    activeItemId: number | null;
    /** Whether batch-selection mode is active */
    isSelectionMode: boolean;
    /** Set of selected document IDs (selection mode) */
    selectedIds: Set<number>;
    /** Total document count across all visible files */
    totalDocCount: number;
    /** Count of documents that can be selected for export */
    selectableDocCount: number;
    /** Current sort key */
    sortBy: string;
    /** Search input value */
    searchQuery: string;
    /** Active status filter keys */
    statusFilter: Set<string>;
    /** Active type filter keys */
    typeFilter: Set<string>;
    /** Active organization filter keys */
    orgFilter: Set<string>;
    /** Dynamic type options (fetched from API) */
    typeOptions: FilterOption[];
    /** Dynamic organization options (populated from file data) */
    orgOptions: FilterOption[];
    /** Callbacks */
    onActivateDocument: (docId: number) => void;
    onToggleSelectMode: () => void;
    onToggleDocSelection: (docId: number) => void;
    onSelectAll: () => void;
    onSearchChange: (query: string) => void;
    onStatusFilterChange: (keys: Set<string>) => void;
    onTypeFilterChange: (keys: Set<string>) => void;
    onOrgFilterChange: (keys: Set<string>) => void;
    onSortChange: (sort: string) => void;
    onUploadClick: () => void;
    /** Open the user profile modal */
    onProfile?: () => void;
    /** Sign out the current user */
    onLogout?: () => void;
    /** Navigate back to the admin console (only shown to ACOM admins in preview mode) */
    onBackToAdmin?: () => void;
    /** Whether the client has a bridge configured (hides bridge indicator when false) */
    hasBridge?: boolean;
    /** Whether bridge is down (disables upload) */
    isBridgeDead?: boolean;
    /** Whether user has no organization assignment (disables upload) */
    hasNoOrgs?: boolean;
    /** Bridge health state passed from HomeApp so the indicator shares the same status. */
    bridgeStatus?: BridgeStatus;
    bridgeLastTimestamp?: string | null;
    bridgeIsRefreshing?: boolean;
    onBridgeRefresh?: () => void;
}

export function LeftPanel({
    userName,
    userInitials,
    sourceFiles,
    activeItemId,
    isSelectionMode,
    selectedIds,
    totalDocCount: _totalDocCount,
    selectableDocCount,
    sortBy,
    searchQuery,
    statusFilter,
    typeFilter,
    orgFilter,
    typeOptions,
    orgOptions,
    onActivateDocument,
    onToggleSelectMode,
    onToggleDocSelection,
    onSelectAll,
    onSearchChange,
    onStatusFilterChange,
    onTypeFilterChange,
    onOrgFilterChange,
    onSortChange,
    onUploadClick,
    onProfile,
    onLogout,
    onBackToAdmin,
    hasBridge = true,
    isBridgeDead = false,
    hasNoOrgs = false,
    bridgeStatus,
    bridgeLastTimestamp,
    bridgeIsRefreshing,
    onBridgeRefresh,
}: LeftPanelProps) {
    const allSelected = selectableDocCount > 0 && selectedIds.size === selectableDocCount;
    const someSelected = selectedIds.size > 0 && !allSelected;

    return (
        <div
            className="relative z-30 flex h-full shrink-0 flex-col border-r border-secondary bg-primary"
            style={{ width: "320px", minWidth: "320px", maxWidth: "320px" }}
        >
            {/* ---- Header ---- */}
            <div className="space-y-3 border-b border-tertiary p-4">
                {/* Top bar: Avatar dropdown + Bridge indicator + Select button */}
                <div className="flex items-center justify-between">
                    <Dropdown.Root>
                        <AriaButton className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 outline-hidden transition duration-100 ease-linear hover:bg-secondary">
                            <Avatar size="xs" initials={userInitials || "U"} />
                            <span className="text-sm font-semibold text-primary">{userName || "User"}</span>
                            <ChevronDown className="size-3.5 text-fg-quaternary" />
                        </AriaButton>
                        <Dropdown.Popover>
                            <Dropdown.Menu>
                                <Dropdown.Item icon={User01} label="Profile" onAction={onProfile} />
                                {onBackToAdmin && (
                                    <>
                                        <Dropdown.Separator />
                                        <Dropdown.Item icon={ArrowLeft} label="Back to Admin" onAction={onBackToAdmin} />
                                    </>
                                )}
                                <Dropdown.Separator />
                                <Dropdown.Item icon={LogOut01} label="Log out" onAction={onLogout} />
                            </Dropdown.Menu>
                        </Dropdown.Popover>
                    </Dropdown.Root>
                    <div className="flex items-center gap-2">
                        {hasBridge && (
                            <BridgeHealthIndicator
                                status={bridgeStatus}
                                lastTimestamp={bridgeLastTimestamp}
                                isRefreshing={bridgeIsRefreshing}
                                onRefresh={onBridgeRefresh}
                            />
                        )}
                        <Button
                            size="sm"
                            color={isSelectionMode ? "primary" : "secondary"}
                            iconLeading={isSelectionMode ? XClose : CursorClick01}
                            onClick={onToggleSelectMode}
                        >
                            {isSelectionMode ? "Cancel" : "Select"}
                        </Button>
                    </div>
                </div>

                {/* Search */}
                <Input
                    icon={SearchLg}
                    placeholder="Search vendors, files..."
                    size="sm"
                    value={searchQuery}
                    onChange={onSearchChange}
                    aria-label="Search documents"
                />

                {/* Filters row (includes select-all checkbox when in selection mode) */}
                <div className="flex min-h-[32px] items-center gap-2">
                    {isSelectionMode && <Checkbox isSelected={allSelected} isIndeterminate={someSelected} onChange={onSelectAll} size="md" />}
                    <FilterDropdown label="Status" options={STATUS_FILTER_OPTIONS} selectedKeys={statusFilter} onSelectionChange={onStatusFilterChange} />
                    {typeOptions.length > 0 && (
                        <FilterDropdown label="Type" options={typeOptions} selectedKeys={typeFilter} onSelectionChange={onTypeFilterChange} />
                    )}
                    {orgOptions.length > 0 && (
                        <FilterDropdown label="Org" options={orgOptions} selectedKeys={orgFilter} onSelectionChange={onOrgFilterChange} />
                    )}
                    <div className="ml-auto">
                        <Dropdown.Root>
                            <AriaButton
                                aria-label="Sort documents"
                                className={cx(
                                    "flex size-8 cursor-pointer items-center justify-center rounded-lg border outline-hidden transition duration-100 ease-linear",
                                    sortBy !== "newest"
                                        ? "border-brand bg-brand-primary text-fg-brand-primary"
                                        : "border-secondary bg-primary text-fg-quaternary hover:border-primary hover:text-fg-tertiary",
                                )}
                            >
                                <SwitchVertical01 className="size-4" />
                            </AriaButton>
                            <Dropdown.Popover>
                                <Dropdown.Menu
                                    selectedKeys={[sortBy]}
                                    onSelectionChange={(keys) => {
                                        const key = [...keys][0] as string;
                                        if (key) onSortChange(key);
                                    }}
                                >
                                    <Dropdown.Item id="newest" label="Newest" />
                                    <Dropdown.Item id="oldest" label="Oldest" />
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown.Root>
                    </div>
                </div>
            </div>

            {/* ---- Document List (scrollable) ---- */}
            <div className="custom-scrollbar flex-1 overflow-y-auto px-2 py-1">
                {sourceFiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                        <FeaturedIcon icon={Package} color="gray" theme="light" size="lg" />
                        <p className="mt-3 text-sm font-medium text-tertiary">No results found</p>
                        <p className="mt-1 text-xs text-quaternary">Try adjusting your search or filters</p>
                    </div>
                ) : (
                    sourceFiles.map((sf) => (
                        <div key={sf.id} className="mb-3">
                            <FileGroupHeader name={sf.fileName} />
                            <div className="space-y-0.5">
                                {sf.documents.map((doc) => (
                                    <DocumentListItem
                                        key={doc.id}
                                        doc={doc}
                                        isActive={doc.id === activeItemId}
                                        isSelectionMode={isSelectionMode}
                                        isSelected={selectedIds.has(doc.id)}
                                        isDisabled={isSelectionMode && !isDocumentSelectable(doc)}
                                        onSelect={() => onToggleDocSelection(doc.id)}
                                        onActivate={() => onActivateDocument(doc.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* ---- Upload Button (pinned bottom) ---- */}
            <div className="shrink-0 border-t border-tertiary px-4 py-3" title={hasNoOrgs ? "You are not assigned to any organization" : undefined}>
                <Button size="md" color="primary" iconLeading={Upload01} className="w-full" isDisabled={isBridgeDead || hasNoOrgs} onClick={onUploadClick}>
                    Upload
                </Button>
            </div>
        </div>
    );
}
